import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Building2,
  Activity,
  AlertTriangle,
  TrendingUp,
  Sparkles,
  Mail,
  CheckSquare,
  CalendarPlus,
  StickyNote,
  Send,
  ClipboardList,
  Target,
  Loader2,
  XCircle,
  CheckCircle2,
  Pencil,
  ArrowRight,
  UserPlus,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { useApiQuery } from '../../hooks/useApiQuery';

/**
 * PendingActionPreviewModal — full AI Action Preview screen (spec §6).
 *
 * Renders the 7 sections required by the spec:
 *   1. Account Summary (name, MRR, health, lifecycle)
 *   2. Why Triggered
 *   3. Impact ($)
 *   4. Goal pill
 *   5. AI Generated Action (parameters)
 *   6. Confidence
 *   7. Action buttons (Edit / Approve & Execute / Reject)
 *
 * Falls back gracefully when optional fields are absent — older actions
 * created before the spec §6 schema extension will still render.
 *
 * @param {object} props
 * @param {object} props.action       Pending action object from the API.
 * @param {boolean} props.open
 * @param {function} props.onOpenChange
 * @param {function} props.onApprove  Async (id) => void
 * @param {function} props.onReject   Async (id) => void
 * @param {function} [props.onResolveExecution] Async (id, outcome, note) => void
 * @param {function} [props.onEdit]   Optional () => void to switch to edit mode.
 *                                    TODO(phase-6): wire inline parameter edit.
 * @param {boolean} [props.canManage] Whether approval controls are available.
 * @param {boolean} [props.canReject] Whether rejection is available.
 * @param {boolean} [props.canResolveExecution] Whether manual resolution is available.
 */
const ACTION_TYPE_META = {
  create_task: { label: 'Create Task', icon: CheckSquare, color: 'text-blue-600' },
  update_task: { label: 'Update Task', icon: ClipboardList, color: 'text-purple-600' },
  send_email: { label: 'Send Email', icon: Mail, color: 'text-green-600' },
  add_note: { label: 'Add Note', icon: StickyNote, color: 'text-amber-600' },
  log_activity: { label: 'Log Activity', icon: Activity, color: 'text-cyan-600' },
  send_survey: { label: 'Send Survey', icon: Send, color: 'text-teal-600' },
  create_contact: { label: 'Create Contact', icon: UserPlus, color: 'text-orange-600' },
  create_meeting: { label: 'Create Meeting', icon: CalendarPlus, color: 'text-indigo-600' },
  generate_qbr: { label: 'Generate QBR', icon: FileText, color: 'text-rose-600' },
  refresh_health_score: { label: 'Refresh Health Score', icon: RefreshCw, color: 'text-lime-700' },
};

const GOAL_LABEL = {
  retention: 'Retention',
  expansion: 'Expansion',
  renewal: 'Renewal',
  re_engage: 'Re-engage',
  reengagement: 'Re-engage',
  'at-risk': 'Re-engage',
  onboarding: 'Onboarding',
};

const PendingActionPreviewModal = ({
  action,
  open,
  onOpenChange,
  onApprove,
  onReject,
  onEdit,
  onResolveExecution,
  canManage = false,
  canReject = canManage,
  canResolveExecution = false,
}) => {
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');

  useEffect(() => {
    setResolutionNote('');
  }, [open, action?.id]);

  // Cached fetches via TanStack Query — deduped across cards in the same session
  const accountId = action?.account_id;
  const enabled = Boolean(open && accountId);

  const { data: account, loading: loadingAccount } = useApiQuery(
    accountId ? `/accounts/${accountId}` : null,
    { deps: [accountId], enabled, showErrorToast: false, staleTime: 60_000 }
  );
  const { data: healthData } = useApiQuery(
    accountId ? `/accounts/${accountId}/health` : null,
    { deps: [accountId], enabled, showErrorToast: false, staleTime: 60_000 }
  );
  // Outcome attribution. Only fetched once the action has executed
  // so we don't pay the round-trip while it's still pending.
  const isExecuted = action?.status === 'executed';
  // useApiQuery already disables when URL is null, so we only need `enabled`
  // for the additional `open` gating. staleTime bumped to 5 min — outcome
  // metrics drift slowly and we don't want to re-hammer the API on re-open.
  const { data: outcome } = useApiQuery(
    action?.id ? `/pending-actions/${action.id}/outcome` : null,
    { deps: [action?.id, isExecuted], enabled: open && isExecuted, showErrorToast: false, staleTime: 5 * 60_000 }
  );

  if (!action) return null;

  const meta = ACTION_TYPE_META[action.action_type] || {
    label: action.action_type,
    icon: ClipboardList,
    color: 'text-muted-foreground',
  };
  const ActionIcon = meta.icon;
  const isPending = action.status === 'pending';

  const handleApprove = async () => {
    if (!onApprove) return;
    setApproving(true);
    try {
      await onApprove(action.id);
      setApproving(false);
      onOpenChange?.(false);
    } catch {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!onReject) return;
    setRejecting(true);
    try {
      await onReject(action.id);
      setRejecting(false);
      onOpenChange?.(false);
    } catch {
      setRejecting(false);
    }
  };

  const handleResolve = async (outcome) => {
    if (!onResolveExecution || !resolutionNote.trim()) return;
    setResolving(true);
    try {
      await onResolveExecution(action.id, outcome, resolutionNote.trim());
      setResolutionNote('');
      onOpenChange?.(false);
    } catch {
      // The mutation hook reports the API error.
    } finally {
      setResolving(false);
    }
  };

  const formatMoney = (v) => {
    if (v === undefined || v === null || Number.isNaN(Number(v))) return null;
    return `$${Math.abs(Number(v)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const accountMrr = formatMoney(account?.monthly_recurring_revenue);
  // Health lives in /accounts/{id}/health, not on the Account model itself
  const healthScore =
    healthData?.current_score ??
    healthData?.overall_score ??
    account?.health_score ??
    null;
  const lifecycle = account?.lifecycle_stage;
  const goalKey = (action.goal || '').toLowerCase();
  const goalLabel = GOAL_LABEL[goalKey] || (action.goal ? action.goal : null);
  const impactAmount = formatMoney(action.impact_amount);
  const impactKind = action.impact_kind || (impactAmount ? 'risk' : null);
  const confidencePct = typeof action.confidence === 'number'
    ? Math.max(0, Math.min(100, Math.round(action.confidence * 100)))
    : null;
  const executionUnknownDetail = action.result?.status === 'survey_created_email_unconfirmed'
    ? 'Survey created; email delivery could not be confirmed.'
    : action.result?.status === 'email_delivery_unconfirmed'
      ? 'Email delivery could not be confirmed.'
      : action.error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            AI Action Preview
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* SECTION 1 — Account Summary */}
          <Section title="Account" icon={Building2}>
            {loadingAccount ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading account…
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Stat label="Name" value={action.account_name || account?.company_name || account?.name || '—'} />
                <Stat label="MRR" value={accountMrr || '—'} />
                <Stat
                  label="Health"
                  value={healthScore !== null && healthScore !== undefined ? Math.round(Number(healthScore)).toString() : '—'}
                />
                <Stat label="Lifecycle" value={lifecycle || '—'} capitalize />
              </div>
            )}
          </Section>

          {/* SECTION 2 — Why Triggered */}
          <Section title="Why this action" icon={AlertTriangle}>
            {action.trigger_reason && (
              <Badge variant="outline" className="mb-2 text-2xs">{action.trigger_reason}</Badge>
            )}
            {action.caleb_reasoning ? (
              <p className="text-sm text-foreground whitespace-pre-wrap">{action.caleb_reasoning}</p>
            ) : (
              <p className="text-xs text-muted-foreground italic">No reasoning recorded.</p>
            )}
          </Section>

          {/* SECTION 3 — Impact */}
          <Section title="Impact" icon={TrendingUp}>
            {impactAmount ? (
              <div className="flex items-center gap-2">
                <span className={`text-lg font-semibold ${impactKind === 'opportunity' ? 'text-green-600' : 'text-red-600'}`}>
                  {impactAmount}
                </span>
                <span className="text-xs text-muted-foreground">
                  {impactKind === 'opportunity' ? 'expansion opportunity' : 'MRR at risk'}
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                Impact estimate not available for this action.
              </p>
            )}
          </Section>

          {/* SECTION 4 — Goal */}
          {goalLabel && (
            <Section title="Goal" icon={Target}>
              <Badge className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs">
                {goalLabel}
              </Badge>
            </Section>
          )}

          {/* SECTION 5 — AI-generated action */}
          <Section
            title="Proposed action"
            icon={ActionIcon}
            iconClass={meta.color}
            badge={meta.label}
          >
            <ActionParams actionType={action.action_type} params={action.parameters} />
          </Section>

          {/* SECTION 6 — Confidence */}
          <Section title="AI confidence" icon={Activity}>
            {confidencePct !== null ? (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">{confidencePct}%</span>
                  <span className="text-muted-foreground">{confidencePct >= 80 ? 'High' : confidencePct >= 60 ? 'Medium' : 'Low'}</span>
                </div>
                <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${confidencePct >= 80 ? 'bg-green-600' : confidencePct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${confidencePct}%` }}
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">Confidence not recorded for this action.</p>
            )}
          </Section>

          {/* Status banners (after execution) */}
          {action.status === 'rejected' && (
            <div className="flex items-center gap-2 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-2">
              <XCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-medium">Action rejected</span>
            </div>
          )}
          {action.status === 'executed' && (
            <ExecutedOutcomeBanner outcome={outcome} />
          )}
          {action.status === 'failed' && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2">
              <XCircle className="h-4 w-4 text-red-600 mt-0.5" />
              <div className="text-xs text-red-800 dark:text-red-300">
                <span className="font-medium">Execution failed</span>
                {action.error && <p className="mt-0.5">{action.error}</p>}
              </div>
            </div>
          )}
          {action.status === 'execution_unknown' && (
            <div className="space-y-3 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-3 py-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-xs text-amber-800 dark:text-amber-300">
                  <span className="font-medium">Execution Outcome Needs Review</span>
                  {executionUnknownDetail && (
                    <p className="mt-0.5">{executionUnknownDetail}</p>
                  )}
                </div>
              </div>
              {canResolveExecution && (
                <div className="space-y-1.5">
                  <Label htmlFor={`resolution-note-${action.id}`}>Review Note</Label>
                  <Textarea
                    id={`resolution-note-${action.id}`}
                    rows={3}
                    value={resolutionNote}
                    onChange={(event) => setResolutionNote(event.target.value)}
                    placeholder="Record how the external outcome was verified"
                    maxLength={1000}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECTION 7 — Actions */}
        {(canManage || canReject) && isPending && (
          <DialogFooter className="gap-2 sm:gap-2">
            {canManage && onEdit && (
              <Button variant="outline" onClick={onEdit} className="gap-1.5">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            )}
            {canReject && (
              <Button
                variant="outline"
                onClick={handleReject}
                disabled={approving || rejecting}
                className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
              >
                {rejecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                Reject
              </Button>
            )}
            {canManage && (
              <Button
                onClick={handleApprove}
                disabled={approving || rejecting}
                className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
              >
                {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                Approve & Execute
              </Button>
            )}
          </DialogFooter>
        )}
        {canResolveExecution && action.status === 'execution_unknown' && (
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="destructive"
              onClick={() => handleResolve('failed')}
              disabled={resolving || !resolutionNote.trim()}
            >
              {resolving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Mark Failed
            </Button>
            <Button
              onClick={() => handleResolve('executed')}
              disabled={resolving || !resolutionNote.trim()}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              {resolving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Mark Executed
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Local building blocks
// ---------------------------------------------------------------------------

const Section = ({ title, icon: Icon, iconClass = 'text-muted-foreground', badge, children }) => (
  <section className="rounded-lg border border-border bg-card p-3">
    <header className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`h-3.5 w-3.5 ${iconClass}`} />}
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
      </div>
      {badge && <Badge variant="secondary" className="text-2xs">{badge}</Badge>}
    </header>
    <div>{children}</div>
  </section>
);

const Stat = ({ label, value, capitalize }) => (
  <div>
    <p className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`text-sm font-medium text-foreground ${capitalize ? 'capitalize' : ''} truncate`}>{value}</p>
  </div>
);

/**
 * Outcome attribution banner.
 * Shown for executed actions; renders a "Health 62 → 71" chip plus an
 * MRR delta chip when both before- and after-snapshots are available.
 * Falls back to a plain "Action executed" banner when no snapshot was
 * captured (older actions, org-wide actions, snapshot failures).
 */
const ExecutedOutcomeBanner = ({ outcome }) => {
  // Hide chips when delta is exactly zero — "62 → 62" is noise. Apply
  // consistently to both Health and MRR.
  const hasHealthDelta =
    outcome &&
    typeof outcome.before_health === 'number' &&
    typeof outcome.current_health === 'number' &&
    outcome.health_delta !== 0;
  const hasMrrDelta =
    outcome &&
    typeof outcome.before_mrr === 'number' &&
    typeof outcome.current_mrr === 'number' &&
    outcome.mrr_delta !== 0;

  if (!hasHealthDelta && !hasMrrDelta) {
    return (
      <div className="flex items-center gap-2 rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-3 py-2">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <span className="text-xs text-green-800 dark:text-green-300 font-medium">Action executed</span>
      </div>
    );
  }

  const fmtMoney = (v) =>
    `$${Math.abs(Math.round(Number(v))).toLocaleString()}`;

  return (
    <div className="rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-3 py-2 space-y-1.5">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-green-600" />
        <span className="text-xs text-green-800 dark:text-green-300 font-medium">
          Action executed — outcome attributed
        </span>
      </div>
      <div className="flex flex-wrap gap-2 pl-6">
        {hasHealthDelta && (
          <DeltaChip
            label="Health"
            before={Math.round(Number(outcome.before_health))}
            after={Math.round(Number(outcome.current_health))}
            delta={outcome.health_delta}
          />
        )}
        {hasMrrDelta && (
          <DeltaChip
            label="MRR"
            before={fmtMoney(outcome.before_mrr)}
            after={fmtMoney(outcome.current_mrr)}
            delta={outcome.mrr_delta}
          />
        )}
      </div>
    </div>
  );
};

const DeltaChip = ({ label, before, after, delta }) => {
  const positive = typeof delta === 'number' && delta > 0;
  const negative = typeof delta === 'number' && delta < 0;
  const tone = positive
    ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 border-green-300 dark:border-green-700'
    : negative
    ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200 border-red-300 dark:border-red-700'
    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700';
  const direction = positive ? 'increased' : negative ? 'decreased' : 'unchanged';
  return (
    <span
      className={`inline-flex items-center gap-1 text-2xs font-medium border rounded-full px-2 py-0.5 ${tone}`}
      aria-label={`${label} ${direction} from ${before} to ${after}`}
    >
      <span className="opacity-70">{label}</span>
      <span>{before}</span>
      <ArrowRight className="h-3 w-3" aria-hidden="true" />
      <span>{after}</span>
    </span>
  );
};

const ActionParams = ({ actionType, params }) => {
  if (!params || Object.keys(params).length === 0) {
    return <p className="text-xs text-muted-foreground italic">No parameters.</p>;
  }
  const rows = [];
  if (actionType === 'send_email') {
    if (params.subject) rows.push(['Subject', params.subject]);
    if (params.to || params.contact_email) rows.push(['To', params.to || params.contact_email]);
    if (params.body) rows.push(['Body', params.body, true]);
  } else if (actionType === 'create_task' || actionType === 'update_task') {
    if (params.title) rows.push(['Title', params.title]);
    if (params.priority) rows.push(['Priority', params.priority]);
    if (params.due_date) rows.push(['Due', new Date(params.due_date).toLocaleDateString()]);
    if (params.description) rows.push(['Description', params.description, true]);
  } else if (actionType === 'create_meeting') {
    if (params.title) rows.push(['Title', params.title]);
    if (params.date) rows.push(['Date', params.date]);
    if (params.time) rows.push(['Time', params.time]);
    if (params.duration) rows.push(['Duration', `${params.duration} min`]);
    if (params.notes) rows.push(['Notes', params.notes, true]);
  } else if (actionType === 'add_note') {
    if (params.content) rows.push(['Note', params.content, true]);
  } else if (actionType === 'log_activity') {
    if (params.activity_type) rows.push(['Type', params.activity_type.replace(/_/g, ' ')]);
    if (params.summary) rows.push(['Summary', params.summary, true]);
    if (params.notes) rows.push(['Notes', params.notes, true]);
    if (params.activity_date) rows.push(['Date', params.activity_date]);
    if (params.duration_minutes !== null && params.duration_minutes !== undefined) {
      rows.push(['Duration', `${params.duration_minutes} min`]);
    }
  } else if (actionType === 'send_survey') {
    if (params.survey_name) rows.push(['Survey', params.survey_name]);
  } else {
    Object.entries(params).slice(0, 6).forEach(([k, v]) => rows.push([k, String(v)]));
  }

  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground italic">No parameters.</p>;
  }

  return (
    <div className="space-y-1.5">
      {rows.map(([k, v, multiline], i) => (
        <div key={i} className={multiline ? '' : 'flex gap-2'}>
          <span className="text-2xs uppercase tracking-wide text-muted-foreground shrink-0 w-20">{k}</span>
          {multiline ? (
            <pre className="mt-1 text-xs text-foreground whitespace-pre-wrap bg-muted/40 rounded p-2 max-h-48 overflow-y-auto">{v}</pre>
          ) : (
            <span className="text-xs text-foreground truncate flex-1">{v}</span>
          )}
        </div>
      ))}
    </div>
  );
};

export default PendingActionPreviewModal;
